import React from 'react';
import ReactDOM from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import './styles/design-tokens.css';
import './styles/global.css';
import { isOrganizerHost, shouldTrackAnalytics } from './config/host';
import { disablePostHog, loadPostHog, runAfterFirstRender } from './lib/analytics/posthogClient';
import { initializePwaInstall } from './lib/pwaInstall';
import { registerSwUpdateHandle } from './lib/swControl';
import { buildPostHogConfig } from './utils/webVitalsPolicy';

// The PostHog project API key is a PUBLIC, write-only client key — it ships in
// every browser bundle by design and cannot read data, so hard-coding it as a
// fallback is safe. The Vercel env var was once saved with a trailing newline;
// the capture endpoint accepted those events (HTTP 200) but silently dropped
// them before ingestion, so every browser event was lost. Trim defensively and
// fall back to the literal values so a malformed/missing env var can never
// break analytics again.
const POSTHOG_KEY = (
  import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN || 'phc_BvWvYEzJ4aa8VwSPkzDck6QjYy2JXErzWZ7TEE87tZ96'
).trim();
const POSTHOG_HOST = (
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'
).trim();

// Missing Vite env alone cannot disable PostHog: production has a literal fallback key.
// Skip initialization on loopback and preview-tunnel hosts, regardless of where the bundle was built.
// PostHog is NOT in the entry chunk (T206-FE): lib/analytics/posthogClient queues every call made
// before the library is there and loadPostHog() downloads it after the first render (see below).
if (shouldTrackAnalytics()) {
  // The initial document path is read now: the Web Vitals minimisation in before_send keeps it
  // even if the library is initialised after a client-side navigation.
  const initialPath = window.location.pathname;
  // Count anonymous traffic from the very first visit WITHOUT writing any
  // cookie/localStorage — cookieless aggregate analytics don't require consent.
  // `identified_only` avoids creating person profiles for anonymous visitors.
  // Granting consent later upgrades persistence to 'localStorage+cookie'
  // (see ConsentContext). Persistence, profiles and the Web Vitals capture
  // (forced locally, minimised in before_send) live in buildPostHogConfig (T110-INT).
  // Web Vitals still reach PostHog from a late init: its web-vitals observers are `buffered`,
  // so LCP/CLS/INP entries recorded before init are replayed.
  // `app_build` tells which bundle a visit runs (a visit without it is an older bundle),
  // `display_mode` separates installed PWA launches from browser tabs. Both feed the nav-v2
  // activation timing (docs/MIGRAZIONE_API.md).
  runAfterFirstRender(() => {
    void loadPostHog({
      apiKey: POSTHOG_KEY,
      config: buildPostHogConfig({ apiHost: POSTHOG_HOST, initialPath }),
      superProperties: {
        app_build: typeof __APP_BUILD__ === 'string' ? __APP_BUILD__ : 'unknown',
        display_mode:
          window.matchMedia?.('(display-mode: standalone)').matches ||
          (navigator as Navigator & { standalone?: boolean }).standalone
            ? 'standalone'
            : 'browser',
      },
    });
  });
} else {
  disablePostHog();
}

// Service worker gating by host: the organizer console gains nothing from a
// service worker (it precaches the whole SPA bundle) and Cache Storage is
// scoped per-origin, so registering it on the organizer host is pure waste.
// injectRegister is disabled in vite.config.ts for both hosts, so registration
// only happens here, explicitly, for the public host.
if (isOrganizerHost()) {
  // Also drop the manifest link so the organizer area cannot be "installed"
  // as a PWA — index.html ships the link unconditionally for both hosts.
  document.querySelector('link[rel="manifest"]')?.remove();
} else {
  const stopPwaInstall = initializePwaInstall();
  import.meta.hot?.dispose(stopPwaInstall);
  // T016-FE: keep the plugin's update handle so the upgrade/maintenance state
  // can offer a CONTROLLED reload (activate waiting worker + purge public
  // snapshots + reload) instead of a plain location.reload() that a stale
  // worker could answer with the same old bundle. `registerType: 'autoUpdate'`
  // in vite.config.ts is what makes this handle actually wait-and-reload.
  const updateSW = registerSW({ immediate: true });
  registerSwUpdateHandle(updateSW);
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to find the root element');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
